import React, { useState, useEffect } from 'react';
import { useForm, SubmitHandler, Controller } from 'react-hook-form';
import { useQueryClient } from 'react-query';
import * as Yup from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';
import { Button, TextField, Checkbox, FormControlLabel } from '@mui/material';
import { enqueueSnackbar } from 'notistack';

import { translate as t } from 'hooks/useTranslations';
import useVerticals from 'data_sources/useVerticals';
import { cloneAccreditationVersion } from '../../../_data_sources/AccreditationVersions/manageAccreditationVersions';
import { type ServerErrorResponse } from 'data_sources/constants';
import Modal from 'components/_layouts/Modal';
import errorHelper from 'helpers/errorHelper';
import { AccreditationBody, AccreditationVersion } from '../../../_types/awb';
import JurisdictionPicker from 'components/_pickers/JurisdictionPicker';
import VerticalPicker from 'components/_pickers/VerticalPicker';
import { getAccreditationVersion } from '../../../_data_sources/AccreditationVersions/useAccreditationVersion';

export type CloneVersionProps = {
  sourceVersionId: string;
  accreditingBody: AccreditationBody;
  setLastId: React.Dispatch<React.SetStateAction<string | null>>;
  setCloneId: React.Dispatch<React.SetStateAction<string | null>>;
};

type FormValues = {
  version: string;
  abbreviation: string;
  jurisdictions: string[];
  verticalIds: string[];
  nationwide: boolean;
};

const CloneVersion: React.FC<CloneVersionProps> = ({
  sourceVersionId,
  accreditingBody,
  setLastId,
  setCloneId,
}: CloneVersionProps) => {
  const queryClient = useQueryClient();
  const [serverErrors, setServerErrors] = useState<ServerErrorResponse>();
  const [isSaving, setIsSaving] = useState(false);
  const [sourceVersion, setSourceVersion] = useState<AccreditationVersion | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);
  const verticalsQuery = useVerticals();

  const accreditingBodyVersionschema = Yup.object().shape({
    version: Yup.string()
      .trim()
      .transform((value: string, originalValue: string) => {
        return value.replace(/\s+/g, ' ');
      })
      .required(`${t('awb.accreditingBodyVersions.properties.version')} is required`),
    abbreviation: Yup.string()
      .trim()
      .transform((value: string, originalValue: string) => {
        return value.replace(/\s+/g, ' ');
      })
      .required(`${t('awb.accreditingBodyVersions.properties.abbreviation')} is required`),
    jurisdictions: Yup.array()
      .of(Yup.string().required())
      .transform((value, originalValue) => {
        return originalValue.filter((x: string) => x.length > 0);
      })
      .test('jurisdictions', 'At least one jurisdiction is required', function (value) {
        return this.parent.nationwide || (value && value.length > 0);
      }),
    verticalIds: Yup.array()
      .of(Yup.string().required())
      .min(1, `${t('awb.accreditingBodyVersions.properties.vertical')} is required`),
    nationwide: Yup.boolean().default(false),
  }) as Yup.ObjectSchema<FormValues>;

  const {
    control,
    register,
    handleSubmit,
    formState: { errors, isValid },
    reset,
    trigger,
    watch,
    setValue,
  } = useForm<FormValues>({
    defaultValues: {
      version: '',
      abbreviation: '',
      jurisdictions: [],
      nationwide: false,
      verticalIds: [],
    },
    resolver: yupResolver(accreditingBodyVersionschema),
    mode: 'onChange',
  });

  const nationwide = watch('nationwide');

  useEffect(() => {
    const fetchSourceVersion = async () => {
      try {
        setIsLoading(true);
        const data = await getAccreditationVersion(accreditingBody.id!, sourceVersionId);
        setSourceVersion(data);
        setError(null);
      } catch (err) {
        setError(err as Error);
      } finally {
        setIsLoading(false);
      }
    };

    if (sourceVersionId && accreditingBody.id) {
      fetchSourceVersion();
    }
  }, [sourceVersionId, accreditingBody.id]);

  useEffect(() => {
    if (sourceVersion) {
      // Set nationwide flag first
      setValue('nationwide', sourceVersion.nationwide ?? false);

      // Only set jurisdictions if not nationwide
      if (!sourceVersion.nationwide) {
        const jurisdictionIds = sourceVersion.jurisdictions.map((j) => (typeof j === 'string' ? j : j.id));
        setValue('jurisdictions', jurisdictionIds);
      }

      const ids = sourceVersion.verticals?.length
        ? sourceVersion.verticals
        : sourceVersion.verticalId
          ? [sourceVersion.verticalId]
          : [];
      if (ids.length) {
        setValue('verticalIds', ids);
      }
    }
  }, [sourceVersion, setValue]);

  const handleSave: SubmitHandler<FormValues> = (values: FormValues) => {
    setIsSaving(true);
    const dataToSend: AccreditationVersion = {
      ...values,
      jurisdictions: values.nationwide ? [] : values.jurisdictions,
    };
    cloneAccreditationVersion(
      accreditingBody.id!,
      sourceVersionId,
      dataToSend,
      (data) => {
        reset();
        setServerErrors(undefined);
        queryClient.invalidateQueries(['accreditation_versions']);
        if (data.id) setLastId(data.id);
        setIsSaving(false);
        setCloneId(null);
        enqueueSnackbar(t('awb.accreditingBodyVersions.clone.success'), { variant: 'success' });
      },
      (errors: ServerErrorResponse) => {
        setIsSaving(false);
        setServerErrors(errors);
      }
    );
  };

  if (isLoading) {
    return <div>Loading...</div>;
  }

  if (error) {
    return <div>Error loading source version</div>;
  }

  return (
    <Modal
      title={`${t('awb.accreditingBodyVersions.clone.title')} - ${sourceVersion?.version}`}
      onSubmit={handleSubmit(handleSave)}
      size='md'
      actions={
        <>
          <Button
            color='neutral'
            onClick={() => setCloneId(null)}
          >
            {t('common.cancel')}
          </Button>
          <Button
            onClick={() => {
              trigger();
            }}
            disabled={isSaving || !isValid}
            loading={isSaving}
            loadingPosition='end'
            component='button'
            type='submit'
            variant='contained'
          >
            {t('common.save')}
          </Button>
        </>
      }
      open={true}
      onClose={() => setCloneId(null)}
    >
      {errorHelper.renderAllErrors(serverErrors, errors)}
      <TextField
        id='version'
        key='version'
        label={t('awb.accreditingBodyVersions.properties.version')}
        variant='outlined'
        required
        fullWidth
        {...register('version')}
        error={!!errors.version}
        helperText={errors.version ? errors.version.message : undefined}
      />
      <TextField
        id='abbreviation'
        key='abbreviation'
        label={t('awb.accreditingBodyVersions.properties.abbreviation')}
        variant='outlined'
        required
        fullWidth
        {...register('abbreviation')}
        error={!!errors.abbreviation}
        helperText={errors.abbreviation ? errors.abbreviation.message : undefined}
      />
      <Controller
        name='jurisdictions'
        control={control}
        render={({ field }) => (
          <JurisdictionPicker
            allowMultiple
            id='jurisdictions'
            {...field}
            label={t('awb.accreditingBodyVersions.properties.jurisdictions')}
            value={field.value}
            onChange={(value) => {
              field.onChange(value);
            }}
            required={!nationwide}
            disabled={true}
            error={!!errors.jurisdictions}
            helperText={errors.jurisdictions ? errors.jurisdictions.message : undefined}
          />
        )}
      />
      <FormControlLabel
        control={
          <Controller
            name='nationwide'
            control={control}
            render={({ field }) => (
              <Checkbox
                {...field}
                checked={field.value}
                disabled={true}
              />
            )}
          />
        }
        label={t('awb.accreditingBodyVersions.properties.nationwide')}
      />
      <Controller
        name='verticalIds'
        control={control}
        render={({ field }) => (
          <VerticalPicker
            id='verticalIds'
            {...field}
            label={t('awb.accreditingBodyVersions.properties.vertical')}
            value={field.value ?? []}
            onChange={field.onChange}
            allowMultiple
            required
            disabled
            error={!!errors.verticalIds}
            helperText={errors.verticalIds ? errors.verticalIds.message : undefined}
          />
        )}
      />
    </Modal>
  );
};

export default CloneVersion;
